import React, { useMemo } from 'react';
import { Badge } from '../ui/Badge';
import { Target, AlertCircle, TrendingUp, Info } from 'lucide-react';
import type { QuestionClassification } from '../../types/workflowAnalysis';

interface ClassificationBadgesProps {
  classification: QuestionClassification;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  className?: string;
}

// Helper to parse inferred_domains which may be a JSON string or array
const parseInferredDomains = (domains: unknown): Array<string | { domain: string }> => {
  if (!domains) return [];

  // If it's already an array, return it
  if (Array.isArray(domains)) return domains;

  // If it's a string, try to parse it as JSON
  if (typeof domains === 'string') {
    try {
      const parsed = JSON.parse(domains);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // If JSON parse fails, it might be a single domain string
      return [domains];
    }
  }

  return [];
};

export const ClassificationBadges: React.FC<ClassificationBadgesProps> = ({
  classification,
  size = 'md',
  showTooltip = true,
  className = ''
}) => {
  // Parse inferred_domains once
  const inferredDomains = useMemo(
    () => parseInferredDomains(classification.inferred_domains),
    [classification.inferred_domains]
  );
  // Priority color mapping
  const getPriorityVariant = (priority: string): 'error' | 'warning' | 'info' => {
    switch (priority.toLowerCase()) {
      case 'high':
      case 'critical':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
      default:
        return 'info';
    }
  };

  // Category/Type icon mapping
  const getCategoryIcon = (category: string) => {
    const categoryLower = category.toLowerCase();

    if (categoryLower.includes('hypothesis')) return Target;
    if (categoryLower.includes('predict')) return TrendingUp;
    if (categoryLower.includes('optim')) return AlertCircle;

    return Info;
  };

  const CategoryIcon = getCategoryIcon(classification.strategic_category);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {/* Strategic Category */}
      <Badge variant="secondary" className="flex items-center gap-1">
        <CategoryIcon className="w-3 h-3" />
        {classification.strategic_category.replace(/_/g, ' ')}
      </Badge>

      {/* Question Type */}
      <Badge variant="primary">
        {classification.question_type.replace(/_/g, ' ')}
      </Badge>

      {/* Priority */}
      <Badge variant={getPriorityVariant(classification.priority)}>
        {classification.priority.toUpperCase()} PRIORITY
      </Badge>

      {/* Inferred Domains (if any) */}
      {inferredDomains.length > 0 && (
        <>
          {inferredDomains.slice(0, 3).map((domain, index) => (
            <Badge key={index} variant="info" className="text-xs">
              {/* Handle both string and object formats */}
              {typeof domain === 'string' ? domain : domain.domain}
            </Badge>
          ))}
          {inferredDomains.length > 3 && (
            <span className="text-xs text-neutral-500">
              +{inferredDomains.length - 3} more
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default ClassificationBadges;
